/**
 * 热门 MCP Skills 组件（完全独立）
 *
 * 与 TrendingCards 功能类似，但显示 MCP Skills
 */

import React from 'react';
import { Skill } from '../api/skillApi';

// 热门 MCP Skills 列表（模拟官方热门）
export const TRENDING_SKILLS: Skill[] = [
  {
    id: 'filesystem',
    name: 'Filesystem',
    publisher: 'ModelContextProtocol',
    description: 'Read and write files on the local filesystem. Perfect for document management, log analysis, and file operations.',
    categories: ['System', 'File Management'],
    tags: ['files', 'filesystem', 'io', 'document'],
    homepage: 'https://github.com/modelcontextprotocol/servers/tree/main/src/filesystem',
    repository: 'https://github.com/modelcontextprotocol/servers',
    license: 'MIT',
    rating: 4.8,
    downloads: '50K+',
    useCases: ['Document management', 'Log analysis', 'File operations'],
    installation: 'npx @modelcontextprotocol/server-filesystem',
    quickStart: 'Install and run MCP Filesystem server for local file access'
  },
  {
    id: 'github',
    name: 'GitHub',
    publisher: 'ModelContextProtocol',
    description: 'Interact with GitHub repositories, issues, pull requests, and more. Ideal for development workflows.',
    categories: ['Development', 'Git'],
    tags: ['github', 'git', 'repository', 'issues', 'pr', 'code'],
    homepage: 'https://github.com/modelcontextprotocol/servers/tree/main/src/github',
    repository: 'https://github.com/modelcontextprotocol/servers',
    license: 'MIT',
    rating: 4.9,
    downloads: '45K+',
    useCases: ['Repository management', 'Issue tracking', 'Code review'],
    installation: 'npx @modelcontextprotocol/server-github',
    quickStart: 'Connect GitHub to enable repository operations in AI assistants'
  },
  {
    id: 'postgres',
    name: 'PostgreSQL',
    publisher: 'ModelContextProtocol',
    description: 'Query and manage PostgreSQL databases with natural language. Support for complex queries and data analysis.',
    categories: ['Database', 'SQL'],
    tags: ['database', 'sql', 'postgresql', 'postgres', 'query'],
    homepage: 'https://github.com/modelcontextprotocol/servers/tree/main/src/postgres',
    repository: 'https://github.com/modelcontextprotocol/servers',
    license: 'MIT',
    rating: 4.7,
    downloads: '35K+',
    useCases: ['Data analysis', 'Report generation', 'Database monitoring'],
    installation: 'npx @modelcontextprotocol/server-postgres',
    quickStart: 'Enable natural language database queries and management'
  },
  {
    id: 'slack',
    name: 'Slack',
    publisher: 'ModelContextProtocol',
    description: 'Send messages and read channels in Slack. Automate notifications and team communication.',
    categories: ['Communication', 'Messaging'],
    tags: ['slack', 'messaging', 'communication', 'notification'],
    homepage: 'https://github.com/modelcontextprotocol/servers/tree/main/src/slack',
    repository: 'https://github.com/modelcontextprotocol/servers',
    license: 'MIT',
    rating: 4.6,
    downloads: '30K+',
    useCases: ['Team notifications', 'Message automation', 'Channel monitoring'],
    installation: 'npx @modelcontextprotocol/server-slack',
    quickStart: 'Integrate Slack for AI-powered team communication'
  },
  {
    id: 'brave-search',
    name: 'Brave Search',
    publisher: 'ModelContextProtocol',
    description: 'Search the web using Brave Search API. Get real-time web search results in your AI assistant.',
    categories: ['Search', 'Web'],
    tags: ['search', 'web', 'brave', 'api', 'internet'],
    homepage: 'https://github.com/modelcontextprotocol/servers/tree/main/src/brave-search',
    repository: 'https://github.com/modelcontextprotocol/servers',
    license: 'MIT',
    rating: 4.5,
    downloads: '25K+',
    useCases: ['Web research', 'Information gathering', 'News updates'],
    installation: 'npx @modelcontextprotocol/server-brave-search',
    quickStart: 'Add web search capabilities to your AI assistant'
  },
  {
    id: 'fetch',
    name: 'Fetch',
    publisher: 'ModelContextProtocol',
    description: 'Make HTTP requests to any URL. Connect AI assistants to external APIs and web services.',
    categories: ['Network', 'HTTP'],
    tags: ['http', 'fetch', 'web', 'api', 'request'],
    homepage: 'https://github.com/modelcontextprotocol/servers/tree/main/src/fetch',
    repository: 'https://github.com/modelcontextprotocol/servers',
    license: 'MIT',
    rating: 4.8,
    downloads: '40K+',
    useCases: ['API integration', 'Web scraping', 'Data fetching'],
    installation: 'npx @modelcontextprotocol/server-fetch',
    quickStart: 'Enable AI to interact with any HTTP API'
  },
  {
    id: 'sqlite',
    name: 'SQLite',
    publisher: 'ModelContextProtocol',
    description: 'Query and manage SQLite databases. Lightweight database operations for AI assistants.',
    categories: ['Database', 'SQL'],
    tags: ['database', 'sql', 'sqlite', 'query', 'local'],
    homepage: 'https://github.com/modelcontextprotocol/servers/tree/main/src/sqlite',
    repository: 'https://github.com/modelcontextprotocol/servers',
    license: 'MIT',
    rating: 4.6,
    downloads: '28K+',
    useCases: ['Local database', 'Data storage', 'Lightweight queries'],
    installation: 'npx @modelcontextprotocol/server-sqlite',
    quickStart: 'Add local SQLite database capabilities'
  },
  {
    id: 'exa',
    name: 'Exa',
    publisher: 'ModelContextProtocol',
    description: 'AI-powered web search with intelligent results. Get relevant and curated web content.',
    categories: ['Search', 'AI'],
    tags: ['search', 'ai', 'web', 'intelligence', 'research'],
    homepage: 'https://github.com/modelcontextprotocol/servers/tree/main/src/exa',
    repository: 'https://github.com/modelcontextprotocol/servers',
    license: 'MIT',
    rating: 4.4,
    downloads: '20K+',
    useCases: ['Smart search', 'Research', 'Content discovery'],
    installation: 'npx @modelcontextprotocol/server-exa',
    quickStart: 'AI-powered web search with better results'
  }
];

interface TrendingSkillsProps {
  onSkillClick: (skillName: string) => void;
  lang?: 'zh' | 'en';
}

export default function TrendingSkills({ onSkillClick, lang = 'zh' }: TrendingSkillsProps) {
  return (
    <div className="max-w-7xl mx-auto">
      {/* 标题 */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          ✨ {lang === 'zh' ? '热门 MCP Skills' : 'Trending MCP Skills'}
        </h2>
        <div className="text-xs text-purple-600 dark:text-purple-400">
          {lang === 'zh' ? '官方推荐' : 'Official Picks'}
        </div>
      </div>

      {/* Skills 卡片网格 */}
      <div className="grid grid-cols-2 gap-6 max-w-5xl mx-auto">
        {TRENDING_SKILLS.map((skill) => (
          <div
            key={skill.id}
            className="group relative bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-5 border border-purple-200 dark:border-purple-700 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            onClick={() => onSkillClick(skill.name)}
          >
            {/* 技能名称 */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-base text-gray-800 dark:text-gray-200 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                  {skill.name}
                </h3>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  by <span className="text-purple-600 dark:text-purple-400">{skill.publisher}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-yellow-400 text-sm">⭐</span>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {skill.rating}
                </span>
              </div>
            </div>

            {/* 描述 */}
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-4 min-h-[60px] leading-relaxed">
              {skill.description}
            </p>

            {/* 分类 */}
            <div className="flex flex-wrap gap-1.5 mb-3">
              {skill.categories.slice(0, 2).map((category, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full"
                >
                  {category}
                </span>
              ))}
            </div>

            {/* 安装命令 */}
            <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-2.5 mb-3">
              <code className="text-xs text-green-400 font-mono truncate block">
                {skill.installation}
              </code>
            </div>

            {/* 底部信息 */}
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 border-t border-purple-200 dark:border-purple-800 pt-3">
              <div className="flex items-center gap-2">
                <span>📥 {skill.downloads}</span>
                <span>📦 {skill.license}</span>
              </div>
              <div className="text-purple-600 dark:text-purple-400">
                {lang === 'zh' ? '点击搜索' : 'Click to search'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
